import { ChangeDetectionStrategy, Component, computed, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { EventQuotaInspect } from "../../../pojo/EventQuotaInspect";
import { EventQuotaService } from "../../../services/event.quota.service";
import { UserService } from "../../../services/user.service";
import { TelemetryEvent, TelemetryService } from "../../../services/telemetry.service";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25EventQuotaInspectorDetailComponent } from "./s25.event.quota.inspector.detail.component";
import { S25OccurrenceObjectScheduleComponent } from "../../../standalone/occurrence-object-schedule/s25.occurrence.object.schedule.component";
import {
    createDefaultScheduleScenario,
    createInitialScheduleValue,
    toQuotaInspectLocationPairs,
} from "../../../standalone/occurrence-object-schedule/occurrence-object-schedule.util";
import type { OccurrenceScheduleValue } from "../../../standalone/occurrence-object-schedule/occurrence-object-schedule.types";

type SummaryRow = {
    rowKey: string;
    quota: EventQuotaInspect.InspectQuotaResult;
    occurrence: EventQuotaInspect.InspectQuotaOccurrence;
    usageLabel: string;
    statusLabel: string;
    statusClass: string;
};

@TypeManagerDecorator("s25-ng-event-quota-inspector")
@Component({
    selector: "s25-ng-event-quota-inspector",
    host: { class: "quota-inspector-host" },
    imports: [
        CommonModule,
        FormsModule,
        S25MultiselectModule,
        S25LoadingInlineModule,
        S25EventQuotaInspectorDetailComponent,
        S25OccurrenceObjectScheduleComponent,
    ],
    template: `
        <div class="quota-inspector">
            <section class="quota-inspector__scenario card">
                <h2 class="quota-inspector__heading">Inspect usage</h2>
                <p class="ngFinePrint">
                    Simulate a booking scenario for a user and see which occurrences count toward each applicable quota.
                </p>

                <div class="quota-inspector__field">
                    <label class="quota-inspector__label">
                        User <span class="quota-inspector__required" aria-hidden="true">*</span>
                    </label>
                    @if (contactPickerBean) {
                        <s25-ng-multiselect-search-criteria
                            [type]="'contacts'"
                            [buttonText]="'Select user'"
                            [modelBean]="contactPickerBean"
                            [selectedItems]="selectedContact()"
                            [showResults]="true"
                            [popoverOnBody]="true"
                            [useSecurity]="false"
                            (close)="onContactPickerClose($event)"
                        />
                    }
                    @if (contactFieldError()) {
                        <p class="ngRed" role="alert">{{ contactFieldError() }}</p>
                    }
                </div>

                <div class="quota-inspector__field">
                    <label class="quota-inspector__label">
                        Organization <span class="ngFinePrint">(optional)</span>
                    </label>
                    @if (organizationPickerBean) {
                        <s25-ng-multiselect-search-criteria
                            [type]="'organizations'"
                            [buttonText]="'Select organization'"
                            [singleSelect]="true"
                            [modelBean]="organizationPickerBean"
                            [selectedItems]="selectedOrganization()"
                            [showResults]="true"
                            [popoverOnBody]="true"
                            (close)="onOrganizationPickerClose($event)"
                        />
                    }
                </div>

                <s25-ng-occurrence-object-schedule [(model)]="schedule" [config]="{ objectKinds: ['locations'] }" />

                <div class="quota-inspector__actions">
                    <button
                        type="button"
                        class="aw-button aw-button--primary"
                        [disabled]="runDisabled()"
                        (click)="runInspection()"
                    >
                        Run inspection
                    </button>
                </div>

                @if (displayError()) {
                    <div class="quota-inspector__error" role="alert">{{ displayError() }}</div>
                }
            </section>

            @if (inspectResource.isLoading()) {
                <s25-loading-inline [model]="{ text: 'Running inspection…' }" />
            }

            @if (inspectResult(); as result) {
                @if (!result.quotaOverride.quotaEnforcementActive) {
                    <div class="quota-inspector__banner" role="status">
                        {{ result.quotaOverride.message }}
                        Breakdown below shows what would count if quotas were enforced.
                    </div>
                }

                @if (!summaryRows().length) {
                    <p class="quota-inspector__empty">No quotas apply to this scenario.</p>
                } @else {
                    <table class="quota-inspector__summary">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Quota</th>
                                <th>Scope</th>
                                <th>Occurrence</th>
                                <th>Usage</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (row of summaryRows(); track row.rowKey) {
                                <tr [class.quota-inspector__row--expanded]="expandedRowKey() === row.rowKey">
                                    <td>
                                        <button
                                            type="button"
                                            class="quota-inspector__expand"
                                            [attr.aria-expanded]="expandedRowKey() === row.rowKey"
                                            [attr.aria-label]="expandedRowKey() === row.rowKey ? 'Collapse' : 'Expand'"
                                            (click)="toggleExpanded(row.rowKey)"
                                        >
                                            {{ expandedRowKey() === row.rowKey ? "▾" : "▸" }}
                                        </button>
                                    </td>
                                    <td>{{ row.quota.quotaName }}</td>
                                    <td>{{ row.quota.scope === "org" ? "Organization" : "User" }}</td>
                                    <td>#{{ row.occurrence.occId }}</td>
                                    <td>{{ row.usageLabel }}</td>
                                    <td [class]="row.statusClass">{{ row.statusLabel }}</td>
                                </tr>
                                @if (expandedRowKey() === row.rowKey) {
                                    <tr class="quota-inspector__detail-row">
                                        <td colspan="6">
                                            <s25-ng-event-quota-inspector-detail
                                                [occurrence]="row.occurrence"
                                                [countByEvent]="row.quota.countByEvent"
                                            />
                                        </td>
                                    </tr>
                                }
                            }
                        </tbody>
                    </table>
                }
            }
        </div>
    `,
    styles: `
        .quota-inspector {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }

        .quota-inspector__scenario {
            padding: 1rem;
        }

        .quota-inspector__heading {
            margin: 0 0 0.5rem;
            font-size: 1.125rem;
        }

        .quota-inspector__field {
            margin-top: 1rem;
        }

        .quota-inspector__label {
            display: block;
            font-weight: 600;
            margin-bottom: 0.35rem;
        }

        .quota-inspector__required {
            color: var(--color--danger-text);
        }

        .quota-inspector__actions {
            margin-top: 1rem;
        }

        .quota-inspector__error {
            margin-top: 0.75rem;
            color: var(--color--danger-text, #b42318);
        }

        .quota-inspector__banner {
            padding: 0.75rem 1rem;
            border: 1px solid var(--color--border);
            background: var(--color--surface-2-bg);
        }

        .quota-inspector__summary {
            width: 100%;
            border-collapse: collapse;
        }

        .quota-inspector__summary th,
        .quota-inspector__summary td {
            border: 1px solid var(--color--border);
            padding: 0.5rem;
            text-align: left;
            vertical-align: top;
        }

        .quota-inspector__expand {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 1rem;
            padding: 0 0.25rem;
        }

        .quota-inspector__status--blocked {
            color: var(--color--danger-text);
            font-weight: 600;
        }

        .quota-inspector__status--exempt {
            font-style: italic;
        }

        .quota-inspector__status--ok {
            color: var(--color--success-text);
        }

        .quota-inspector__detail-row td {
            background: var(--color--surface-2-bg);
        }

        .quota-inspector__empty {
            font-style: italic;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25EventQuotaInspectorComponent {
    readonly contactPickerBean: MultiselectModelI = {
        selectedItems: [],
        singleSelect: true,
    };
    readonly organizationPickerBean: MultiselectModelI = {
        selectedItems: [],
        singleSelect: true,
    };
    readonly eventTypePickerBean: MultiselectModelI = {
        selectedItems: [],
        singleSelect: true,
    };

    readonly selectedContact = signal<S25ItemI[]>([]);
    readonly selectedOrganization = signal<S25ItemI[]>([]);
    readonly selectedEventType = signal<S25ItemI[]>([]);
    private readonly defaultScenario = createDefaultScheduleScenario();
    readonly schedule = signal<OccurrenceScheduleValue>(
        createInitialScheduleValue(this.defaultScenario.startDt, this.defaultScenario.endDt),
    );
    readonly runError = signal<string | undefined>(undefined);
    private readonly contactSubmitAttempted = signal(false);
    readonly expandedRowKey = signal<string | undefined>(undefined);
    readonly inspectRequest = signal<EventQuotaInspect.InspectRequest | undefined>(undefined);

    readonly inspectResource = s25Resource({
        params: () => this.inspectRequest(),
        loader: async ({ params }) => {
            if (!params) {
                return null;
            }
            return EventQuotaService.inspect(params);
        },
    });

    readonly inspectResult = computed(() => this.inspectResource.value());

    readonly displayError = computed((): string | undefined => {
        const clientError = this.runError();
        if (clientError) {
            return clientError;
        }
        const loadError = this.inspectResource.error();
        if (loadError instanceof Error) {
            return loadError.message;
        }
        if (loadError) {
            return String(loadError);
        }
        return undefined;
    });

    readonly selectedContactName = computed(() => {
        const contact = this.selectedContact()[0];
        return contact?.itemName;
    });

    readonly contactFieldError = computed((): string | undefined => {
        if (!this.contactSubmitAttempted()) {
            return undefined;
        }
        const contact = this.selectedContact()[0];
        if (contact?.itemId) {
            return undefined;
        }
        return "Select a user to inspect.";
    });

    readonly summaryRows = computed((): SummaryRow[] => {
        const result = this.inspectResult();
        if (!result?.quotas?.length) {
            return [];
        }
        const rows: SummaryRow[] = [];
        for (const quota of result.quotas) {
            for (const occurrence of quota.occurrences) {
                const primaryWindow = this.primaryWindow(occurrence);
                rows.push({
                    rowKey: `${quota.scope}-${quota.quotaId}-${occurrence.occId}`,
                    quota,
                    occurrence,
                    usageLabel: this.usageLabel(quota, primaryWindow),
                    statusLabel: this.statusLabel(occurrence, result.quotaOverride.quotaEnforcementActive),
                    statusClass: this.statusClass(occurrence, result.quotaOverride.quotaEnforcementActive),
                });
            }
        }
        return rows;
    });

    readonly runDisabled = computed(() => this.inspectResource.isLoading());

    onContactPickerClose(selectedItems: S25ItemI[]): void {
        this.selectedContact.set(S25Util.array.forceArray(selectedItems).slice(0, 1));
        if (this.selectedContact()[0]?.itemId) {
            this.contactSubmitAttempted.set(false);
        }
    }

    onOrganizationPickerClose(selectedItems: S25ItemI[]): void {
        this.selectedOrganization.set(S25Util.array.forceArray(selectedItems).slice(0, 1));
    }

    onEventTypePickerClose(selectedItems: S25ItemI[]): void {
        this.selectedEventType.set(S25Util.array.forceArray(selectedItems).slice(0, 1));
    }

    async runInspection(): Promise<void> {
        this.runError.set(undefined);
        this.expandedRowKey.set(undefined);

        const contact = this.selectedContact()[0];
        if (!contact?.itemId) {
            this.contactSubmitAttempted.set(true);
            return;
        }
        this.contactSubmitAttempted.set(false);

        const user = await UserService.getUserByContactId(Number(contact.itemId));
        const targetUsername = user?.username ?? user?.r25_username;
        if (!targetUsername) {
            this.runError.set("Selected contact does not have a 25Live user account.");
            return;
        }

        const organization = this.selectedOrganization()[0];
        const eventType = this.selectedEventType()[0];
        const scheduleValue = this.schedule();
        const occurrences = scheduleValue?.occurrences ?? [];
        const eventSpan = this.eventSpanFromOccurrences(occurrences);
        const request: EventQuotaInspect.InspectRequest = {
            targetUsername,
            organizationId: organization?.itemId ? Number(organization.itemId) : undefined,
            eventTypeId: eventType?.itemId ? Number(eventType.itemId) : undefined,
            locationAssignments: toQuotaInspectLocationPairs(scheduleValue?.assignments ?? []),
            dates: occurrences.map((occurrence) => ({
                occId: occurrence.occId,
                startDt: occurrence.startDt,
                endDt: occurrence.endDt,
                eventStartDt: eventSpan?.eventStartDt,
                eventEndDt: eventSpan?.eventEndDt,
            })),
        };

        this.sendTelemetryEvent();
        this.inspectRequest.set({ ...request });
    }

    toggleExpanded(rowKey: string): void {
        this.expandedRowKey.update((current) => (current === rowKey ? undefined : rowKey));
    }

    private primaryWindow(
        occurrence: EventQuotaInspect.InspectQuotaOccurrence,
    ): EventQuotaInspect.InspectWindow | undefined {
        if (!occurrence.windows?.length) {
            return undefined;
        }
        return occurrence.windows.reduce((worst, window) => {
            if (!worst) {
                return window;
            }
            const worstCountRatio = (worst.rsrvCount ?? 0) / (worst.quotaMax || 1);
            const windowCountRatio = (window.rsrvCount ?? 0) / (window.quotaMax || 1);
            return windowCountRatio > worstCountRatio ? window : worst;
        });
    }

    private usageLabel(
        quota: EventQuotaInspect.InspectQuotaResult,
        window: EventQuotaInspect.InspectWindow | undefined,
    ): string {
        if (!window) {
            return "—";
        }
        const countUnit = quota.countByEvent ? "event" : "occ";
        const parts: string[] = [];
        if (window.quotaMax) {
            parts.push(`${window.rsrvCount ?? 0}/${window.quotaMax} ${countUnit}`);
        }
        if (window.durationMax) {
            parts.push(`${window.durationTotal ?? 0}/${window.durationMax} min`);
        }
        if (!parts.length && quota.quantity) {
            parts.push(`${window.rsrvCount ?? 0}/${quota.quantity} ${countUnit}`);
        }
        return parts.join(" · ") || "—";
    }

    private eventSpanFromOccurrences(
        occurrences: { startDt: Date; endDt: Date }[],
    ): { eventStartDt: Date; eventEndDt: Date } | undefined {
        if (!occurrences.length) {
            return undefined;
        }
        let eventStartDt = occurrences[0].startDt;
        let eventEndDt = occurrences[0].endDt;
        for (const occurrence of occurrences) {
            if (occurrence.startDt < eventStartDt) {
                eventStartDt = occurrence.startDt;
            }
            if (occurrence.endDt > eventEndDt) {
                eventEndDt = occurrence.endDt;
            }
        }
        return { eventStartDt, eventEndDt };
    }

    private statusLabel(occurrence: EventQuotaInspect.InspectQuotaOccurrence, enforcementActive: boolean): string {
        if (!enforcementActive) {
            return "Exempt";
        }
        if (!occurrence.allowed) {
            return "Blocked";
        }
        return "Allowed";
    }

    private statusClass(occurrence: EventQuotaInspect.InspectQuotaOccurrence, enforcementActive: boolean): string {
        if (!enforcementActive) {
            return "quota-inspector__status--exempt";
        }
        if (!occurrence.allowed) {
            return "quota-inspector__status--blocked";
        }
        return "quota-inspector__status--ok";
    }

    private sendTelemetryEvent(): void {
        const telemetryEvent: TelemetryEvent = {
            eventCategory: "Quotas",
            subCategory: "QuotaInspect",
            eventType: "Run",
        };
        TelemetryService.sendEvent(telemetryEvent);
    }
}
